import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { UserPlus, ShieldCheck, Truck, User, Lock, RotateCcw, Power, X } from 'lucide-react';
import { authHeaders } from '../lib/authToken';
import { useAuth } from '../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { key: 'admin', label: 'Administrateur', icon: <ShieldCheck size={14} />, color: 'var(--admin-gold)' },
  { key: 'livreur', label: 'Livreur', icon: <Truck size={14} />, color: 'var(--admin-teal)' },
  { key: 'client', label: 'Client', icon: <User size={14} />, color: 'var(--admin-muted)' },
];

function roleConf(role) {
  return ROLES.find(r => r.key === role) || ROLES[2];
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', nom: '', prenom: '', telephone: '', role: 'livreur' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/users`, { headers: authHeaders() });
      if (res.status === 401 || res.status === 403) {
        toast.error('Session expirée — reconnectez-vous');
        setUsers([]); setLoading(false); return;
      }
      if (res.ok) setUsers(await res.json());
    } catch {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setRowBusy = (id, v) => setBusy(b => ({ ...b, [id]: v }));

  async function apiAction(id, path, method, body) {
    setRowBusy(id, true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/users/${id}/${path}`, {
        method,
        headers: authHeaders(body ? { 'Content-Type': 'application/json' } : {}),
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Opération refusée');
        return false;
      }
      toast.success(data.message || 'Fait');
      await load();
      return true;
    } catch {
      toast.error('Erreur réseau');
      return false;
    } finally {
      setRowBusy(id, false);
    }
  }

  async function createUser(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/users`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Création refusée'); setCreating(false); return; }
      toast.success('Utilisateur créé');
      setShowCreate(false);
      setForm({ email: '', password: '', nom: '', prenom: '', telephone: '', role: 'livreur' });
      await load();
    } catch {
      toast.error('Erreur réseau');
    }
    setCreating(false);
  }

  return (
    <div className="max-w-3xl" data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>Utilisateurs & accès</h2>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Comptes admin, livreur et clients — activation, rôle, mot de passe</p>
        </div>
        <button data-testid="users-new-btn" onClick={() => setShowCreate(true)}
          className="btn-gold px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2">
          <UserPlus size={16} /> Nouvel utilisateur
        </button>
      </div>

      {/* Rappel garde anti-verrouillage */}
      <div className="flex items-start gap-2 p-3 rounded-xl mb-4"
        style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
        <Lock size={14} style={{ color: 'var(--admin-gold)', marginTop: 2, flexShrink: 0 }} />
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
          Au moins un administrateur actif doit toujours exister. Vous ne pouvez ni désactiver votre propre compte, ni retirer votre propre rôle admin.
        </p>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-16 rounded-xl animate-pulse" style={{ background: 'var(--admin-surface)' }} />)}
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
          <User size={32} style={{ color: 'var(--admin-faint)', margin: '0 auto 8px' }} />
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Aucun utilisateur</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(u => {
            const rc = roleConf(u.role);
            const isSelf = me && u.id === me.id;
            const disabled = !!u.disabled;
            const locked = !!u.account_locked;
            return (
              <div key={u.id} data-testid={`user-row-${u.id}`}
                className="p-4 rounded-xl flex flex-wrap items-center gap-3"
                style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A', opacity: disabled ? 0.6 : 1 }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${rc.color}18`, color: rc.color }}>{rc.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--admin-text)' }}>
                    {(u.prenom || u.nom) ? `${u.prenom || ''} ${u.nom || ''}`.trim() : u.email}
                    {isSelf && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--admin-muted)' }}>vous</span>}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>{u.email}</p>
                </div>

                <div className="flex items-center gap-1.5">
                  {disabled && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>Désactivé</span>}
                  {locked && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>Verrouillé</span>}
                </div>

                {/* Rôle */}
                <select
                  data-testid={`user-role-${u.id}`}
                  value={u.role || 'client'}
                  disabled={busy[u.id] || isSelf}
                  onChange={(e) => apiAction(u.id, 'role', 'PUT', { role: e.target.value })}
                  className="px-2 py-1.5 rounded-lg text-xs"
                  style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }}>
                  {ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>

                {/* Reset mot de passe */}
                <button data-testid={`user-reset-${u.id}`}
                  onClick={() => apiAction(u.id, 'reset-password', 'POST')}
                  disabled={busy[u.id]}
                  title="Envoyer un lien de réinitialisation"
                  className="p-2 rounded-lg disabled:opacity-40"
                  style={{ background: 'rgba(197,216,249,0.1)', color: '#C5D8F9', border: 'none', cursor: 'pointer' }}>
                  <RotateCcw size={14} />
                </button>

                {/* Activer / Désactiver */}
                {disabled ? (
                  <button data-testid={`user-enable-${u.id}`}
                    onClick={() => apiAction(u.id, 'enable', 'PUT')}
                    disabled={busy[u.id]}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40"
                    style={{ background: 'rgba(34,197,94,0.12)', color: '#22C55E', border: 'none', cursor: 'pointer' }}>
                    <Power size={13} /> Réactiver
                  </button>
                ) : (
                  <button data-testid={`user-disable-${u.id}`}
                    onClick={() => apiAction(u.id, 'disable', 'PUT')}
                    disabled={busy[u.id] || isSelf}
                    title={isSelf ? 'Impossible de désactiver votre propre compte' : ''}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40"
                    style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: 'none', cursor: isSelf ? 'not-allowed' : 'pointer' }}>
                    <Power size={13} /> Désactiver
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal création */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowCreate(false)}>
          <form data-testid="user-create-modal" onSubmit={createUser} onClick={e => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl p-6"
            style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-base" style={{ color: 'var(--admin-text)' }}>Nouvel utilisateur</h3>
              <button type="button" onClick={() => setShowCreate(false)} className="p-1" style={{ background: 'none', border: 'none', color: 'var(--admin-muted)', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="user-prenom-input" placeholder="Prénom" value={form.prenom}
                  onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                  className="px-3 py-2 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
                <input data-testid="user-nom-input" placeholder="Nom" value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  className="px-3 py-2 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
              </div>
              <input data-testid="user-email-input" type="email" required placeholder="Email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
              <input data-testid="user-telephone-input" placeholder="Téléphone (optionnel)" value={form.telephone}
                onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
              <input data-testid="user-password-input" type="password" required minLength={8} placeholder="Mot de passe (8 car. min.)" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
              <select data-testid="user-role-select" value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }}>
                {ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <button data-testid="user-create-submit" type="submit" disabled={creating}
              className="btn-gold w-full mt-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">
              {creating ? 'Création…' : 'Créer le compte'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
